import { Component, useState, useEffect, type ErrorInfo, type ReactNode } from 'react'
import { ConvexProvider, ConvexReactClient, useAction, useMutation, useQuery } from 'convex/react'
import { anyApi } from 'convex/server'
import { mergeModelChain, parseModelList } from '../../convex/lib/modelList'
import { userFacingErrorMessage } from '../../convex/lib/clientErrors'
import {
  entryKey,
  entryLabel,
  resolveChainStart,
  type ChainEntry,
  type ModelClass,
  type RotationStrategy,
} from '../../convex/lib/modelRotation'
import { LLM_PARAM_SPECS, type LlmParamKey } from '../../convex/lib/llmParams'
import { fnv1aHex, planPromptTemplate } from '../../convex/lib/llmPrompt'
import { CHAT_TONES } from '../../convex/lib/chatTone'
// Menu sidebar = SATU spec bersama (dashboard/src/nav.ts): dashboard owner
// memakai `navForRole('owner')` dari file yang sama, jadi tidak ada daftar
// menu kedua yang bisa drift. Dijaga scripts/probe-dashboard-nav.ts.
import {
  dashboardBrand,
  defaultNavKey,
  navForRole,
  navItemFor,
  type DashboardRole,
} from './nav'
import { diffLines, diffSummary } from '../../convex/lib/promptDiff'
import { formatLocalDateTime } from '../../convex/lib/tenantTime'
// Kebijakan retensi log dipakai dari SATU modul backend (bukan angka salinan di
// UI) — bagian §Retensi Log SCHEMA.md dijelaskan di tab 📋 Log.
import {
  AUDIT_ARCHIVE_RETENTION_DAYS,
  AUDIT_DETAIL_RETENTION_DAYS,
  DEBUG_LOG_RETENTION_DAYS,
} from '../../convex/lib/logRetention'

// anyApi membuat function reference runtime (tanpa codegen), aman dari
// stub _generated yang stale — akar masalah blank putih kaskita-1we.
// Lihat scripts/probe-dashboard-api.ts untuk anti-regresi.
const api = anyApi

// Client React (WebSocket) pakai domain .convex.cloud; .convex.site hanya untuk HTTP Actions.
const CONVEX_URL = import.meta.env.VITE_CONVEX_URL ?? 'https://shocking-cat-841.convex.cloud'


const client = new ConvexReactClient(CONVEX_URL)

// ─── Login ─────────────────────────────────────────────
function Login({ onLogin }: { onLogin: (token: string) => void }) {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [error, setError] = useState('')
  const [loading, setLoading] = useState(false)

  const signIn = useMutation(api.auth.signIn)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    setLoading(true)
    setError('')
    try {
      const result = await signIn({ email, password })
      localStorage.setItem('kaskita_session', result.sessionToken)
      onLogin(result.sessionToken)
    } catch (err) {
      // ConvexError dari auth.signIn membawa pesan asli di `err.data`;
      // pesan Error biasa disensor Convex jadi "Server Error" di produksi.
      setError(userFacingErrorMessage(err, 'Login gagal'))
    } finally {
      setLoading(false)
    }
  }

  return (
    <div style={styles.loginWrap}>
      <form onSubmit={handleSubmit} style={styles.loginCard}>
        <h1 style={styles.title}>🔒 KasKita Dashboard</h1>
        <p style={styles.subtitle}>Superadmin AI Gateway</p>
        <input
          type="email"
          placeholder="Email"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          style={styles.input}
          required
        />
        <input
          type="password"
          placeholder="Password"
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          style={styles.input}
          required
        />
        {error && <p style={styles.error}>{error}</p>}
        <button type="submit" disabled={loading} style={styles.btn}>
          {loading ? 'Loading...' : 'Login'}
        </button>
      </form>
    </div>
  )
}

// ─── Provider Card ─────────────────────────────────────
function ProviderCard({ provider, token, chatChain, visionChain }: {
  provider: any
  token: string
  chatChain: ChainEntry[]
  visionChain: ChainEntry[]
}) {
  const [expanded, setExpanded] = useState(false)

  // Query reaktif: begitu mutation menulis, Convex langsung push data baru —
  // tidak ada polling 10 detik (dulu bikin tombol enable/disable terasa lambat).
  const keys: any[] =
    useQuery(api.dashboard.listProviderKeys, {
      sessionToken: token,
      providerSlug: provider.slug,
    }) ?? []

  const toggleProvider = useMutation(api.dashboard.toggleProvider)
  const deleteProvider = useMutation(api.dashboard.deleteProvider)
  const toggleKey = useMutation(api.dashboard.toggleKey)
  const resetCooldown = useMutation(api.dashboard.resetKeyCooldown)
  const deleteKey = useMutation(api.dashboard.deleteKey)

  const chainChat = chatChain.filter((e) => e.provider === provider.slug)
  const chainVision = visionChain.filter((e) => e.provider === provider.slug)

  const handleToggle = async () => {
    await toggleProvider({
      sessionToken: token,
      providerId: provider._id,
      enabled: !provider.enabled,
    })
  }

  // Hapus permanen: backend mencascade semua key milik provider ini.
  const handleDeleteProvider = async () => {
    if (!confirm(`Hapus permanen provider "${provider.slug}" beserta semua key-nya?`)) return
    await deleteProvider({ sessionToken: token, providerId: provider._id })
  }

  const handleKeyToggle = async (keyId: string, enabled: boolean) => {
    await toggleKey({ sessionToken: token, keyId: keyId as any, enabled: !enabled })
  }

  const handleResetCooldown = async (keyId: string) => {
    await resetCooldown({ sessionToken: token, keyId: keyId as any })
  }

  const handleDeleteKey = async (keyId: string) => {
    if (!confirm('Hapus key ini?')) return
    await deleteKey({ sessionToken: token, keyId: keyId as any })
  }

  return (
    <div style={styles.card}>
      <div style={styles.cardHeader} onClick={() => setExpanded(!expanded)}>
        <div>
          <strong>{provider.label}</strong>
          <span style={styles.slug}> ({provider.slug})</span>
        </div>
        <div style={styles.row}>
          <span style={{
            ...styles.badge,
            background: provider.enabled ? '#10b981' : '#ef4444',
          }}>
            {provider.enabled ? 'ENABLED' : 'DISABLED'}
          </span>
          <span style={styles.badgeGray}>P{provider.priority}</span>
          <span style={{ cursor: 'pointer', fontSize: '1.2em' }}>
            {expanded ? '▼' : '▶'}
          </span>
        </div>
      </div>
      <div style={styles.cardBody}>
        <div style={styles.row}>
          <span>Katalog: <strong>{(provider.models as string[]).length}</strong> model</span>
          {chainChat.length > 0 && (
            <span style={styles.badgeGray}>{chainChat.length} di rotasi chat</span>
          )}
          {chainVision.length > 0 && (
            <span style={styles.badgeGray}>{chainVision.length} di rotasi vision</span>
          )}
          <span>Base URL: <code>{provider.baseUrl}</code></span>
        </div>
        <p style={styles.hint}>
          Urutan rotasi model ada di tab <strong>Model</strong> — satu section lintas provider.
        </p>
        <div style={styles.row}>
          <button onClick={handleToggle} style={{
            ...styles.btn,
            flex: 1,
            background: provider.enabled ? '#ef4444' : '#10b981',
          }}>
            {provider.enabled ? 'Disable Provider' : 'Enable Provider'}
          </button>
          <button onClick={handleDeleteProvider} style={{ ...styles.btn, background: '#7f1d1d' }}>
            🗑 Hapus Provider
          </button>
        </div>
      </div>
      {expanded && (
        <div style={styles.keysSection}>
          <h3 style={styles.h3}>Keys ({keys.length})</h3>
          {keys.map((key) => (
            <div key={key._id} style={styles.keyRow}>
              <div style={styles.keyInfo}>
                <strong>{key.label}</strong>
                <span style={styles.slug}> {key.secretPreview}</span>
                <div style={styles.row}>
                  <span style={{
                    ...styles.badge,
                    background: key.enabled ? '#10b981' : '#ef4444',
                  }}>
                    {key.enabled ? 'ON' : 'OFF'}
                  </span>
                  {key.cooldownUntil > Date.now() && (
                    <span style={{ ...styles.badge, background: '#f59e0b' }}>
                      COOLDOWN
                    </span>
                  )}
                  {key.failCount > 0 && (
                    <span style={{ ...styles.badge, background: '#ef4444' }}>
                      FAILS: {key.failCount}
                    </span>
                  )}
                </div>
                {key.lastError && (
                  <div style={styles.errorText}>⚠ {key.lastError}</div>
                )}
              </div>
              <div style={styles.keyActions}>
                <button
                  onClick={() => handleKeyToggle(key._id, key.enabled)}
                  style={styles.btnSm}
                >
                  {key.enabled ? 'Disable' : 'Enable'}
                </button>
                {key.cooldownUntil > 0 && (
                  <button
                    onClick={() => handleResetCooldown(key._id)}
                    style={{ ...styles.btnSm, background: '#f59e0b' }}
                  >
                    Reset CD
                  </button>
                )}
                <button
                  onClick={() => handleDeleteKey(key._id)}
                  style={{ ...styles.btnSm, background: '#ef4444' }}
                >
                  Delete
                </button>
              </div>
            </div>
          ))}
          <AddKeyForm token={token} providerSlug={provider.slug} />
          <AddModelsForm
            token={token}
            providerSlug={provider.slug}
            catalogSize={(provider.models as string[]).length}
          />
        </div>
      )}
    </div>
  )
}

// ─── Add Key Form ───────────────────────────────────────
function AddKeyForm({ token, providerSlug }: { token: string; providerSlug: string }) {
  const [label, setLabel] = useState('')
  const [secret, setSecret] = useState('')
  const [show, setShow] = useState(false)
  const addKey = useMutation(api.dashboard.addKey)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    await addKey({
      sessionToken: token,
      providerSlug,
      label,
      secret,
    })
    setLabel('')
    setSecret('')
  }

  return (
    <form onSubmit={handleSubmit} style={styles.addKeyForm}>
      <h4 style={styles.h4}>+ Add Key</h4>
      <input
        type="text"
        placeholder="Label (e.g., 9router-stb-secondary)"
        value={label}
        onChange={(e) => setLabel(e.target.value)}
        style={styles.input}
        required
      />
      <input
        type={show ? 'text' : 'password'}
        placeholder="API Key / Token"
        value={secret}
        onChange={(e) => setSecret(e.target.value)}
        style={styles.input}
        required
      />
      <div style={styles.row}>
        <button type="submit" style={styles.btn}>Add Key</button>
        <button type="button" onClick={() => setShow(!show)} style={styles.btnSm}>
          {show ? 'Hide' : 'Show'}
        </button>
      </div>
    </form>
  )
}

// ─── Katalog model provider (SHARED helper) ──────────────
// Satu sumber logika: chain aktif per klasifikasi + kandidat model provider.

/** Entri chain GLOBAL milik satu provider (badge CHAT/VISION & panel katalog). */
function chainEntriesFor(chain: ChainEntry[], slug: string): ChainEntry[] {
  return chain.filter((e) => e.provider === slug)
}

/**
 * Kandidat model provider = katalog ∪ model yang SEDANG dipakai chain ini.
 * Model yang dipakai rotasi tapi belum masuk katalog tetap tampil (tidak jadi
 * data tersembunyi) dan bisa didaftarkan lewat picker.
 */
function providerModelOptions(p: any, chain: ChainEntry[]): string[] {
  return mergeModelChain(
    (p.models as string[]) ?? [],
    chainEntriesFor(chain, p.slug).map((e) => e.model)
  )
}

// ─── Add Models (katalog, tanpa klasifikasi) ─────────────
// Klasifikasi chat/vision TIDAK lagi diminta di sini (dulu harus pilih lebih
// dulu). Model masuk katalog provider; pemilihan chat/vision dilakukan di tab
// Model lewat picker "Tambah Model".
function AddModelsForm({ token, providerSlug, catalogSize }: {
  token: string
  providerSlug: string
  catalogSize: number
}) {
  const [raw, setRaw] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [ok, setOk] = useState('')
  const addModels = useMutation(api.dashboard.addModels)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    const models = parseModelList(raw)
    if (models.length === 0) {
      setError('Tidak ada model terbaca dari input')
      return
    }
    setBusy(true)
    setError('')
    setOk('')
    try {
      const res = await addModels({ sessionToken: token, providerSlug, models })
      setRaw('')
      setOk(`+${res.added} model terdaftar di katalog (total: ${res.models.length})`)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Gagal menyimpan model')
    } finally {
      setBusy(false)
    }
  }

  const previewCount = parseModelList(raw).length

  return (
    <form onSubmit={handleSubmit} style={styles.addKeyForm}>
      <h4 style={styles.h4}>+ Tambah ke katalog (pisahkan dengan koma / spasi / baris)</h4>
      <input
        type="text"
        placeholder="kr/qwen3-coder-next, gh/gpt-4.1, gemini/gemini-3.8-flash"
        value={raw}
        onChange={(e) => setRaw(e.target.value)}
        style={styles.input}
      />
      <div style={styles.row}>
        <button type="submit" disabled={busy} style={styles.btn}>
          {busy ? 'Menyimpan...' : 'Tambah ke Katalog'}
        </button>
        <span style={styles.slug}>
          {previewCount > 0
            ? `${previewCount} model terbaca`
            : `katalog saat ini: ${catalogSize} model`}
        </span>
      </div>
      {error && <p style={styles.error}>{error}</p>}
      {ok && <p style={{ ...styles.error, color: '#10b981' }}>{ok}</p>}
    </form>
  )
}

// ─── Sub-tab provider: daftar model terdaftar + hapus ────
function ProviderModelsPanel({ provider, token, chatChain, visionChain }: {
  provider: any
  token: string
  chatChain: ChainEntry[]
  visionChain: ChainEntry[]
}) {
  const deleteProviderModel = useMutation(api.dashboard.deleteProviderModel)
  const toggleProvider = useMutation(api.dashboard.toggleProvider)
  const [error, setError] = useState('')
  const [busy, setBusy] = useState<string | null>(null)
  const [toggling, setToggling] = useState(false)

  const catalog = (provider.models as string[]) ?? []
  const chatModels = chainEntriesFor(chatChain, provider.slug).map((e) => e.model)
  const visionModels = chainEntriesFor(visionChain, provider.slug).map((e) => e.model)
  const list = providerModelOptions(provider, chatChain)

  // Enable/disable langsung dari sub-tab provider (dulu harus balik ke tab Semua).
  const handleToggleEnabled = async () => {
    setToggling(true)
    setError('')
    try {
      await toggleProvider({
        sessionToken: token,
        providerId: provider._id,
        enabled: !provider.enabled,
      })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Gagal mengubah status provider')
    } finally {
      setToggling(false)
    }
  }

  const handleDelete = async (model: string) => {
    if (
      !confirm(
        `Hapus model "${model}" dari ${provider.slug}? Model juga dilepas dari rotasi chat/vision yang memakainya.`
      )
    )
      return
    setBusy(model)
    setError('')
    try {
      await deleteProviderModel({ sessionToken: token, providerSlug: provider.slug, model })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Gagal menghapus model')
    } finally {
      setBusy(null)
    }
  }

  return (
    <div style={styles.card}>
      <div style={styles.cardHeader}>
        <div>
          <strong>{provider.label}</strong>
          <span style={styles.slug}> ({provider.slug})</span>
        </div>
        <div style={styles.row}>
          <span style={{
            ...styles.badge,
            background: provider.enabled ? '#10b981' : '#ef4444',
          }}>
            {provider.enabled ? 'ENABLED' : 'DISABLED'}
          </span>
          <span style={styles.badgeGray}>P{provider.priority}</span>
          <button
            onClick={() => void handleToggleEnabled()}
            disabled={toggling}
            style={{ ...styles.btnSm, background: provider.enabled ? '#ef4444' : '#10b981' }}
          >
            {toggling ? '...' : provider.enabled ? 'Disable' : 'Enable'}
          </button>
        </div>
      </div>
      <div style={styles.cardBody}>
        <h3 style={styles.h3}>📚 Model terdaftar ({list.length})</h3>
        {list.length === 0 && (
          <p style={styles.hint}>Belum ada model. Tambahkan lewat input di bawah.</p>
        )}
        {list.map((m, i) => (
          <div key={m} style={styles.keyRow}>
            <div style={styles.keyInfo}>
              <span style={styles.modelNum}>#{i + 1}</span> <code>{m}</code>
              {chatModels.includes(m) && (
                <span style={{ ...styles.badge, background: '#3b82f6', marginLeft: 8 }}>
                  CHAT #{chatModels.indexOf(m) + 1}
                </span>
              )}
              {visionModels.includes(m) && (
                <span style={{ ...styles.badge, background: '#8b5cf6', marginLeft: 8 }}>
                  VISION #{visionModels.indexOf(m) + 1}
                </span>
              )}
              {!catalog.includes(m) && (
                <span
                  style={{ ...styles.badge, background: '#f59e0b', marginLeft: 8 }}
                  title="Dipakai rotasi tapi belum masuk katalog"
                >
                  rotasi
                </span>
              )}
            </div>
            <div style={styles.keyActions}>
              <button
                onClick={() => void handleDelete(m)}
                disabled={busy === m}
                style={{ ...styles.btnSm, background: '#ef4444' }}
              >
                🗑 Hapus
              </button>
            </div>
          </div>
        ))}
        {error && <p style={styles.error}>{error}</p>}
        <AddModelsForm
          token={token}
          providerSlug={provider.slug}
          catalogSize={catalog.length}
        />
        <p style={styles.hint}>
          Model mana yang dipakai <strong>chat</strong> / <strong>vision</strong> dipilih di tab{' '}
          <strong>Model</strong> — satu chain rotasi lintas provider lewat tombol{' '}
          <strong>➕ Tambah Model</strong>.
        </p>
      </div>
    </div>
  )
}

function chainKey(provider: string, model: string): string {
  return `${provider}::${model}`
}

// ─── Form Tambah Provider ───────────────────────────────
// Provider yang dihapus bisa didaftarkan ulang dari UI (dulu hanya lewat
// seedProviders internal). Model awalnya opsional; chain rotasi diatur di tab
// Model (satu chain lintas provider).
function AddProviderForm({ token }: { token: string }) {
  const [open, setOpen] = useState(false)
  const [slug, setSlug] = useState('')
  const [label, setLabel] = useState('')
  const [baseUrl, setBaseUrl] = useState('')
  const [chatPath, setChatPath] = useState('/chat/completions')
  const [kind, setKind] = useState<'openai-compatible' | 'github-copilot'>('openai-compatible')
  const [models, setModels] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const addProvider = useMutation(api.dashboard.addProvider)

  const submit = async (e: React.FormEvent) => {
    e.preventDefault()
    setBusy(true)
    setError('')
    try {
      await addProvider({
        sessionToken: token,
        slug,
        label,
        kind,
        baseUrl,
        chatPath,
        models: parseModelList(models),
      })
      setSlug('')
      setLabel('')
      setBaseUrl('')
      setModels('')
      setOpen(false)
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Gagal menambah provider')
    } finally {
      setBusy(false)
    }
  }

  if (!open) {
    return (
      <button onClick={() => setOpen(true)} style={{ ...styles.btn, marginBottom: '0.75rem' }}>
        ➕ Tambah Provider
      </button>
    )
  }

  return (
    <form onSubmit={submit} style={styles.addKeyForm}>
      <h4 style={styles.h4}>➕ Provider baru</h4>
      <input type="text" placeholder="slug (mis. 9router)" value={slug}
        onChange={(e) => setSlug(e.target.value)} style={styles.input} required />
      <input type="text" placeholder="Label (mis. 9router STB)" value={label}
        onChange={(e) => setLabel(e.target.value)} style={styles.input} required />
      <input type="text" placeholder="Base URL (https://...)" value={baseUrl}
        onChange={(e) => setBaseUrl(e.target.value)} style={styles.input} required />
      <div style={styles.row}>
        <input type="text" placeholder="/chat/completions" value={chatPath}
          onChange={(e) => setChatPath(e.target.value)} style={styles.input} required />
        <select value={kind} onChange={(e) => setKind(e.target.value as any)} style={styles.select}>
          <option value="openai-compatible">openai-compatible</option>
          <option value="github-copilot">github-copilot</option>
        </select>
      </div>
      <input type="text" placeholder="Model awal (opsional, pisah koma)" value={models}
        onChange={(e) => setModels(e.target.value)} style={styles.input} />
      <div style={styles.row}>
        <button type="submit" disabled={busy} style={styles.btn}>
          {busy ? 'Menyimpan...' : 'Simpan Provider'}
        </button>
        <button type="button" onClick={() => setOpen(false)} disabled={busy} style={styles.btnSm}>
          Batal
        </button>
      </div>
      {error && <p style={styles.error}>{error}</p>}
      <p style={styles.hint}>
        Setelah dibuat: tambah key di kartu provider, lalu pilih modelnya di tab{' '}
        <strong>Model</strong> (chain rotasi lintas provider).
      </p>
    </form>
  )
}

// ─── Picker "Tambah Model" (tab Model) ───────────────────
// Popup centang model dari katalog semua provider. Hanya model DICENTANG yang
// masuk chain rotasi (fallback / round-robin) — satu section lintas provider,
// jadi chat vs vision dipilih di sini, bukan saat input model.
function ModelPickerModal({ providers, kind, chain, token, onClose }: {
  providers: any[]
  kind: ModelClass
  chain: ChainEntry[]
  token: string
  onClose: () => void
}) {
  const setRotationChain = useMutation(api.dashboard.setRotationChain)
  const [picked, setPicked] = useState<string[]>(() => chain.map((e) => chainKey(e.provider, e.model)))
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')

  const isPicked = (providerSlug: string, model: string) =>
    picked.includes(chainKey(providerSlug, model))

  const toggle = (providerSlug: string, model: string) => {
    const key = chainKey(providerSlug, model)
    if (picked.includes(key)) {
      if (picked.length <= 1) return // chain rotasi minimal 1 model
      setPicked(picked.filter((k) => k !== key))
      return
    }
    setPicked([...picked, key])
  }

  const apply = async () => {
    setBusy(true)
    setError('')
    try {
      // Urutan: entri lama yang masih dicentang tetap di posisinya (urutan lintas
      // provider dipertahankan), yang baru menyusul per provider urut katalog.
      const next: ChainEntry[] = chain.filter((e) => isPicked(e.provider, e.model))
      for (const p of providers) {
        for (const m of providerModelOptions(p, chain)) {
          if (!isPicked(p.slug, m)) continue
          if (next.some((e) => e.provider === p.slug && e.model === m)) continue
          next.push({ provider: p.slug, model: m })
        }
      }
      if (next.length === 0) throw new Error('Pilih minimal 1 model')
      await setRotationChain({ sessionToken: token, kind, chain: next })
      onClose()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Gagal menyimpan pilihan model')
    } finally {
      setBusy(false)
    }
  }

  return (
    <div style={styles.modalBackdrop} onClick={busy ? undefined : onClose}>
      <div style={styles.modalCard} onClick={(e) => e.stopPropagation()}>
        <div style={styles.sectionHeader}>
          <h3 style={styles.h3}>
            ➕ Pilih Model Rotasi {kind === 'vision' ? '🖼️ Vision' : '💬 Chat'}
          </h3>
          <button onClick={onClose} disabled={busy} style={styles.btnSm}>
            ✕
          </button>
        </div>
        <p style={styles.hint}>
          Hanya model <strong>dicentang</strong> yang ikut rotasi {kind} (fallback /
          round-robin), terlepas dari provider mana. Urutan diatur setelah ini lewat drag
          &amp; drop di daftar rotasi.
        </p>
        {providers.map((p) => (
          <div key={p._id} style={styles.pickerGroup}>
            <div style={styles.row}>
              <strong>{p.label}</strong>
              <span style={styles.slug}>({p.slug})</span>
              {!p.enabled && <span style={styles.badgeGray}>DISABLED</span>}
              <span style={styles.slug}>
                {providerModelOptions(p, chain).filter((m) => isPicked(p.slug, m)).length} dicentang
              </span>
            </div>
            {providerModelOptions(p, chain).length === 0 && (
              <p style={styles.hint}>
                Katalog kosong — tambah model di sub-tab <strong>{p.slug}</strong>.
              </p>
            )}
            {providerModelOptions(p, chain).map((m) => {
              const checked = isPicked(p.slug, m)
              const lastOne = checked && picked.length <= 1
              return (
                <label
                  key={`${p.slug}-${m}`}
                  style={styles.checkRow}
                  title={lastOne ? 'Minimal 1 model di chain rotasi' : ''}
                >
                  <input
                    type="checkbox"
                    checked={checked}
                    disabled={busy || lastOne}
                    onChange={() => toggle(p.slug, m)}
                  />
                  <code>{entryLabel({ provider: p.slug, model: m })}</code>
                  {!((p.models as string[]) ?? []).includes(m) && (
                    <span style={styles.slug}> (via rotasi)</span>
                  )}
                </label>
              )
            })}
          </div>
        ))}
        {error && <p style={styles.error}>{error}</p>}
        <div style={styles.row}>
          <button onClick={() => void apply()} disabled={busy} style={styles.btn}>
            {busy ? 'Menyimpan...' : '✅ Terapkan'}
          </button>
          <button onClick={onClose} disabled={busy} style={styles.btnSm}>
            Batal
          </button>
        </div>
      </div>
    </div>
  )
}

// ─── Rotasi model: SATU section lintas provider ─────────
// Daftar model digabung dari semua provider; tiap baris diberi prefix
// `provider/model` (gaya combo 9router) supaya asal model tetap terbaca.
function ModelRotation({ providers, token, config }: {
  providers: any[]
  token: string
  config: any
}) {
  const [kind, setKind] = useState<ModelClass>('chat')
  const [pickerOpen, setPickerOpen] = useState(false)
  const [busy, setBusy] = useState(false)
  const [savingSetting, setSavingSetting] = useState(false)
  const [error, setError] = useState('')
  const [drag, setDrag] = useState<number | null>(null)
  const [dropIndex, setDropIndex] = useState<number | null>(null)
  const [stickyDraft, setStickyDraft] = useState('1')

  const setRotationChain = useMutation(api.dashboard.setRotationChain)
  const setRotationStrategy = useMutation(api.dashboard.setRotationStrategy)
  const setRotationSticky = useMutation(api.dashboard.setRotationSticky)

  const setting = config?.[kind] as
    | {
        strategy: RotationStrategy
        sticky: number
        chain: ChainEntry[]
        derived: boolean
        lastModel: string
        stickyLeft: number
      }
    | undefined
  const chain = setting?.chain ?? []
  const strategy: RotationStrategy = setting?.strategy ?? 'fallback'
  const sticky = setting?.sticky ?? 1
  const enabledSlugs = new Set(providers.filter((p: any) => p.enabled).map((p: any) => p.slug))
  // Titik mulai dihitung dengan helper yang SAMA dengan runtime Convex
  // (`resolveChainStart`): index dihitung ulang tiap request dari anchor model
  // terakhir, jadi urutan chain boleh diganti kapan saja tanpa balik ke #1.
  const startIndex = resolveChainStart(
    chain,
    chain,
    setting?.lastModel ?? '',
    setting?.stickyLeft ?? 0
  )
  const lastUsedEntry = chain.find((e) => entryKey(e) === (setting?.lastModel ?? ''))
  const lastUsedLabel = lastUsedEntry ? entryLabel(lastUsedEntry) : ''

  useEffect(() => {
    setStickyDraft(String(sticky))
  }, [kind, sticky])

  const saveChain = async (next: ChainEntry[]) => {
    setBusy(true)
    setError('')
    try {
      await setRotationChain({ sessionToken: token, kind, chain: next })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Gagal menyimpan urutan rotasi')
    } finally {
      setBusy(false)
    }
  }

  const move = (idx: number, dir: -1 | 1) => {
    const target = idx + dir
    if (target < 0 || target >= chain.length || busy) return
    const next = [...chain]
    ;[next[idx], next[target]] = [next[target], next[idx]]
    void saveChain(next)
  }

  const remove = (idx: number) => {
    if (chain.length <= 1) {
      setError('Chain rotasi minimal 1 model')
      return
    }
    void saveChain(chain.filter((_, i) => i !== idx))
  }

  // Drag & drop: pindahkan entri `drag` ke posisi baris tujuan.
  const dropAt = (target: number) => {
    if (drag === null || busy || drag === target) return
    const next = [...chain]
    const [moved] = next.splice(drag, 1)
    next.splice(target, 0, moved)
    void saveChain(next)
  }

  const changeStrategy = async (next: RotationStrategy) => {
    if (next === strategy) return
    setSavingSetting(true)
    setError('')
    try {
      await setRotationStrategy({ sessionToken: token, kind, strategy: next })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Gagal menyimpan strategi')
    } finally {
      setSavingSetting(false)
    }
  }

  const saveSticky = async () => {
    const value = Math.trunc(Number(stickyDraft))
    if (!Number.isFinite(value) || value < 1) {
      setStickyDraft(String(sticky))
      setError('Sticky minimal 1 = jumlah request berturut-turut per titik mulai')
      return
    }
    if (value === sticky) return
    setSavingSetting(true)
    setError('')
    try {
      await setRotationSticky({ sessionToken: token, kind, sticky: value })
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Gagal menyimpan sticky')
    } finally {
      setSavingSetting(false)
    }
  }

  const subtabStyle = (active: boolean) => (active ? styles.subtabActive : styles.subtab)
  const strategyHint =
    strategy === 'round-robin'
      ? `RoundRobin: titik mulai = model terakhir yang melayani, ditahan ${sticky} request (sticky) lalu maju ke model berikutnya — beban merata dan urutan chain boleh diganti kapan saja tanpa rotasi balik ke #1.`
      : 'Fallback: urutan tetap dari PRIMARY ke bawah — pindah model hanya saat model aktif gagal.'

  return (
    <div style={styles.statsSection}>
      <div style={styles.subtabs}>
        <button onClick={() => setKind('chat')} style={subtabStyle(kind === 'chat')}>
          💬 Chat
        </button>
        <button onClick={() => setKind('vision')} style={subtabStyle(kind === 'vision')}>
          🖼️ Vision
        </button>
      </div>
      <div style={styles.sectionHeader}>
        <h2 style={styles.h2}>🔁 Model Rotation</h2>
        <div style={styles.row}>
          <label style={styles.row}>
            <span style={styles.slug}>Strategi {kind}</span>
            <select
              value={strategy}
              onChange={(e) => void changeStrategy(e.target.value as RotationStrategy)}
              disabled={savingSetting}
              style={styles.select}
            >
              <option value="fallback">Fallback</option>
              <option value="round-robin">RoundRobin</option>
            </select>
          </label>
          <label style={styles.row} title="Jumlah request berturut-turut per titik mulai (round-robin)">
            <span style={styles.slug}>Sticky</span>
            <input
              type="number"
              min={1}
              max={50}
              step={1}
              value={stickyDraft}
              onChange={(e) => setStickyDraft(e.target.value)}
              onBlur={() => void saveSticky()}
              onKeyDown={(e) => {
                if (e.key === 'Enter') void saveSticky()
              }}
              disabled={savingSetting}
              style={styles.stickyInput}
            />
            <span style={styles.slug}>req</span>
          </label>
        </div>
      </div>
      <p style={styles.hint}>
        SATU chain rotasi lintas provider (model diganti tanpa peduli provider mana).{' '}
        {strategyHint} Urutan dijalankan Convex dari DB — geser (drag & drop) baris untuk
        mengatur urutan, atau pakai ↑↓ / ✕. Tambah model dari katalog provider lewat tombol{' '}
        <strong>➕ Tambah Model</strong> di bawah.
      </p>
      {error && <p style={styles.error}>{error}</p>}
      <div style={styles.card}>
        <div style={styles.cardHeader}>
          <div>
            <strong>Chain {kind}</strong>
            <span style={styles.slug}>
              {' '}
              {chain.length} model ·{' '}
              {setting?.derived ? 'hasil derivasi provider (belum disimpan)' : 'tersimpan di DB'}
            </span>
          </div>
          {strategy === 'round-robin' && chain.length > 1 && (
            <span
              style={styles.badgeGray}
              title="Model yang dicoba lebih dulu pada request berikutnya (dihitung ulang dari model terakhir yang dipakai — tahan kuat walau urutan chain diubah)"
            >
              ↻ mulai #{startIndex + 1}
              {lastUsedLabel ? ` · terakhir ${lastUsedLabel}` : ''}
            </span>
          )}
        </div>
        <div style={styles.cardBody}>
          {chain.length === 0 && (
            <p style={styles.hint}>
              Belum ada model. Tambah model ke katalog provider dulu (tab Providers), lalu pilih
              di sini.
            </p>
          )}
          {chain.map((entry, i) => (
            <div
              key={chainKey(entry.provider, entry.model)}
              style={{
                ...styles.keyRow,
                ...(drag === i ? styles.keyRowDragging : null),
                ...(dropIndex === i && drag !== i ? styles.keyRowDropTarget : null),
              }}
              draggable={!busy}
              onDragStart={() => setDrag(i)}
              onDragEnter={() => setDropIndex(i)}
              onDragOver={(e) => e.preventDefault()}
              onDrop={(e) => {
                e.preventDefault()
                dropAt(i)
                setDrag(null)
                setDropIndex(null)
              }}
              onDragEnd={() => {
                setDrag(null)
                setDropIndex(null)
              }}
            >
              <div style={styles.keyInfo}>
                <span style={styles.dragHandle} title="Geser untuk ubah urutan">
                  ⠿
                </span>
                <span style={styles.modelNum}>#{i + 1}</span>
                <code>{entryLabel(entry)}</code>
                {i === 0 && (
                  <span style={{ ...styles.badge, background: '#3b82f6', marginLeft: 8 }}>
                    PRIMARY
                  </span>
                )}
                {!enabledSlugs.has(entry.provider) && (
                  <span style={{ ...styles.badge, background: '#ef4444', marginLeft: 8 }}>
                    PROVIDER OFF
                  </span>
                )}
              </div>
              <div style={styles.keyActions}>
                <button
                  onClick={() => move(i, -1)}
                  disabled={busy || i === 0}
                  style={styles.btnSm}
                  title="Naik"
                >
                  ↑
                </button>
                <button
                  onClick={() => move(i, 1)}
                  disabled={busy || i === chain.length - 1}
                  style={styles.btnSm}
                  title="Turun"
                >
                  ↓
                </button>
                <button
                  onClick={() => remove(i)}
                  disabled={busy || chain.length <= 1}
                  style={{ ...styles.btnSm, background: '#ef4444' }}
                  title="Hapus dari rotasi"
                >
                  ✕
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Picker di paling bawah: pilih model dari katalog provider mana pun. */}
      <div style={{ ...styles.row, marginTop: '1rem' }}>
        <button onClick={() => setPickerOpen(true)} disabled={busy} style={styles.btn}>
          ➕ Tambah Model
        </button>
        <span style={styles.slug}>
          Pilih dari katalog provider — hanya yang dicentang ikut rotasi {kind}.
        </span>
      </div>
      {pickerOpen && (
        <ModelPickerModal
          providers={providers}
          kind={kind}
          chain={chain}
          token={token}
          onClose={() => setPickerOpen(false)}
        />
      )}
    </div>
  )
}
// ─── Parameter LLM global (tab ⚙️ LLM, di antara Model dan Usage Stats) ──
// Spesifikasi parameter (label, batas, nilai bawaan) diambil dari
// convex/lib/llmParams.ts — SATU sumber kebenaran dengan runtime Convex, jadi
// input di sini tidak bisa berbeda arti dengan nilai yang benar-benar dipakai
// bot. Kosong = pakai setelan provider lalu bawaan kode (perilaku sekarang).
function LlmSettings({ token, providers }: { token: string; providers: any[] }) {
  const data: any = useQuery(api.dashboard.getLlmSettings, { sessionToken: token })
  const setLlmParam = useMutation(api.dashboard.setLlmParam)
  const [draft, setDraft] = useState<Record<string, string>>({})
  const [busy, setBusy] = useState<string | null>(null)
  const [error, setError] = useState('')
  const [saved, setSaved] = useState('')

  const params: Record<string, number> = data?.params ?? {}
  const defaults: Record<string, number> = data?.defaults ?? {}

  /** Nilai input: draft user → nilai tersimpan → kosong (= pakai bawaan). */
  const draftValue = (key: LlmParamKey) =>
    draft[key] ?? (params[key] !== undefined ? String(params[key]) : '')

  const clearDraft = (key: LlmParamKey) =>
    setDraft((prev) => {
      const next = { ...prev }
      delete next[key]
      return next
    })

  const apply = async (key: LlmParamKey, value: number | null) => {
    setBusy(key)
    setError('')
    setSaved('')
    try {
      await setLlmParam({ sessionToken: token, key, value })
      clearDraft(key)
      setSaved(
        value === null ? `${key} kembali ke bawaan` : `${key} disimpan sebagai ${value}`
      )
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Gagal menyimpan parameter LLM')
    } finally {
      setBusy(null)
    }
  }

  /** Simpan saat blur/Enter. Input dikosongkan = kembali ke bawaan. */
  const commit = async (key: LlmParamKey) => {
    const raw = draftValue(key).trim()
    const wasSet = params[key] !== undefined
    if (raw === '') {
      clearDraft(key)
      if (wasSet) await apply(key, null)
      return
    }
    const value = Number(raw)
    if (!Number.isFinite(value)) {
      clearDraft(key)
      setError(`${key}: nilai harus angka`)
      return
    }
    if (wasSet && params[key] === value) {
      clearDraft(key)
      return
    }
    await apply(key, value)
  }

  /** Provider yang masih mengisi kolom ini sendiri (bawaan seed). */
  const providerOverride = (key: LlmParamKey) => {
    const rows = providers
      .filter((p: any) => p?.[key] !== undefined && p?.[key] !== null)
      .map((p: any) => `${p.slug} ${p[key]}`)
    return rows.length > 0 ? rows.join(' · ') : ''
  }

  return (
    <div style={styles.statsSection}>
      <h2 style={styles.h2}>⚙️ Parameter LLM</h2>
      <p style={styles.hint}>
        Knob global untuk semua model di chain. Kosong = pakai setelan provider lalu bawaan kode
        (perilaku sekarang). Begitu diisi, nilainya menimpa setelan provider dan langsung dipakai
        runtime Convex — tanpa deploy ulang. Simpan dengan Enter atau keluar dari input; kosongkan
        input untuk kembali ke bawaan.
      </p>
      {error && <p style={styles.error}>{error}</p>}
      {saved && <p style={styles.hint}>✅ {saved}</p>}
      {LLM_PARAM_SPECS.map((spec) => {
        const isSet = params[spec.key] !== undefined
        const override = providerOverride(spec.key)
        return (
          <div key={spec.key} style={styles.card}>
            <div style={{ ...styles.cardHeader, cursor: 'default' }}>
              <div>
                <strong>{spec.label}</strong>{' '}
                <span style={styles.slug}>
                  {spec.unit ? `${spec.unit} · ` : ''}bawaan {defaults[spec.key] ?? spec.fallback}
                </span>
                {isSet ? (
                  <span style={{ ...styles.badge, background: '#3b82f6', marginLeft: 8 }}>
                    DISET MANUAL
                  </span>
                ) : (
                  <span style={{ ...styles.badgeGray, marginLeft: 8 }}>BAWAAN</span>
                )}
              </div>
              <div style={styles.row}>
                <input
                  type="number"
                  min={spec.min}
                  max={spec.max}
                  step={spec.step}
                  value={draftValue(spec.key)}
                  placeholder={String(defaults[spec.key] ?? spec.fallback)}
                  onChange={(e) => setDraft((prev) => ({ ...prev, [spec.key]: e.target.value }))}
                  onBlur={() => void commit(spec.key)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') void commit(spec.key)
                  }}
                  disabled={busy === spec.key}
                  style={{ ...styles.stickyInput, width: '7rem' }}
                />
                {isSet && (
                  <button
                    onClick={() => void apply(spec.key, null)}
                    disabled={busy === spec.key}
                    style={styles.btnSm}
                    title="Kembalikan ke bawaan (provider → kode)"
                  >
                    ↺ bawaan
                  </button>
                )}
              </div>
            </div>
            <div style={styles.cardBody}>
              <span style={styles.slug}>{spec.hint}</span>
              {override && (
                <span style={styles.slug}>
                  Nilai provider yang mengisi sendiri: {override} — dipakai selama input ini kosong.
                </span>
              )}
            </div>
          </div>
        )
      })}
    </div>
  )
}

// ─── Usage Stats ────────────────────────────────────────
// Query reaktif: log baru dari bot langsung muncul tanpa polling manual.
// Timestamp kejadian memakai WITA (UTC+8) — zona tetap dari tabel offset di
// convex/lib/tenantTime, jadi tampilan tidak berubah mengikuti timezone browser.
// ─── Tab 👤 User (soft delete + reset onboarding, 5 Okt 2026) ───────────
// List semua user bot yang pernah chat + tombol Hapus (soft delete).
// Delete TIDAK menghapus baris users: FK data uang (transactions.cashierId,
// journalEntries.createdBy, stockLogs.userId) menunjuk ke sinya. Yang terjadi:
// nonaktif + deletedAt + telegramId di-suffix `#deleted:<ts>` supaya akun
// Telegram yang sama bebas /start daftar ulang (uji ulang onboarding).
function UsersPanel({ token }: { token: string }) {
  const users: any[] | undefined = useQuery(api.dashboard.listUsers, { sessionToken: token })
  const deleteUser = useMutation(api.dashboard.deleteUserSoft)

  const handleDeleteUser = async (u: any) => {
    const label = `${u.name} (${u.role}, tenant: ${u.tenantName ?? '-'})`
    if (
      !confirm(
        `Hapus user "${label}"?\n\n` +
          'Akun dinonaktifkan dan akun Telegram yang sama BISA daftar ulang lewat /start.\n' +
          'Transaksi/jurnal lama tetap tersimpan. Aksi ini tercatat di audit log.'
      )
    )
      return
    await deleteUser({ sessionToken: token, userId: u._id })
  }

  if (users === undefined) {
    return <div style={styles.slug}>Memuat data user…</div>
  }
  if (users.length === 0) {
    return <div style={styles.card}>Belum ada user terdaftar.</div>
  }
  return (
    <div>
      <div style={{ ...styles.card, marginBottom: 12 }}>
        <b>{users.filter((u) => !u.deleted).length}</b> user aktif ·{' '}
        <b>{users.filter((u) => u.deleted).length}</b> dihapus (arsip, akun Telegram-nya bebas daftar ulang)
      </div>
      {users.map((u) => (
        <div
          key={u._id}
          style={{
            ...styles.card,
            marginBottom: 12,
            opacity: u.deleted ? 0.55 : 1,
          }}
        >
          <div>
            <b>{u.name}</b> <code style={styles.slug}>{u.role}</code>
            {u.deleted && <span style={styles.slug}> · 🗑 dihapus {formatLocalDateTime(u.deletedAt, GATEWAY_LOG_TIMEZONE)}</span>}
          </div>
          <div style={{ ...styles.slug, ...styles.breakAll }}>
            Tenant: {u.tenantName ?? '-'} · Telegram ID: <code>{u.telegramId}</code> · terdaftar {formatLocalDateTime(u.createdAt, GATEWAY_LOG_TIMEZONE)}
          </div>
          {!u.deleted && (
            <div style={{ marginTop: 8 }}>
              <button onClick={() => handleDeleteUser(u)} style={styles.btnSm}>🗑 Hapus</button>
            </div>
          )}
        </div>
      ))}
    </div>
  )
}

const GATEWAY_LOG_TIMEZONE = 'Asia/Makassar'

/**
 * Panel 🧠 Prompt (8 Okt 2026): menampilkan PERSIS blok FAKTA + system prompt
 * yang dikirim ke LLM untuk satu tenant, supaya grounding bisa diaudit tanpa
 * membedah kode. READ-ONLY — tidak memanggil LLM (tidak membakar kuota key),
 * tidak menulis DB; angka yang tampil dibaca segar saat tombol ditekan.
 *
 * Isinya BUKAN salinan: `dashboard.getTenantPromptPreview` merakit prompt lewat
 * `lib/llmPrompt` + `lib/llmChat` + `telegram/menu` yang sama dengan
 * `botLlm.planMessage`, jadi tidak mungkin drift dari yang benar-benar dikirim.
 */
function PromptPreview({ token }: { token: string }) {
  const tenants: any[] =
    useQuery(api.dashboard.listTenantOptions, { sessionToken: token }) ?? []
  const runPreview = useAction(api.dashboard.getTenantPromptPreview)
  const [tenantId, setTenantId] = useState('')
  const [preview, setPreview] = useState<any>(null)
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const selected = tenantId || tenants[0]?._id || ''

  // ── Riwayat snapshot (📚 deploy) ───────────────────────────────────────────
  const snapshots: any[] =
    useQuery(api.dashboard.listPromptSnapshots, { sessionToken: token }) ?? []
  const [selectedHash, setSelectedHash] = useState<string | null>(null)
  const currentTemplate = planPromptTemplate()
  const currentHash = fnv1aHex(currentTemplate)
  const selectedSnapshot = snapshots.find((s: any) => s.templateHash === selectedHash) ?? null
  const historyLines = selectedSnapshot ? diffLines(selectedSnapshot.template, currentTemplate) : []
  const historySummary = diffSummary(historyLines)

  const load = async () => {
    if (!selected) return
    setBusy(true)
    setError(null)
    try {
      setPreview(await runPreview({ sessionToken: token, tenantId: selected }))
    } catch (err) {
      setPreview(null)
      setError(userFacingErrorMessage(err, 'Gagal memuat prompt'))
    } finally {
      setBusy(false)
    }
  }

  return (
    <div style={styles.statsSection}>
      <h2 style={styles.h2}>🧠 Prompt yang dikirim ke LLM</h2>
      <p style={styles.hint}>
        Dirakit dari sumber yang sama dengan bot (`lib/llmPrompt` + `lib/llmChat` + `telegram/menu`),
        jadi yang tampil = yang benar-benar dikirim ke model. Hanya membaca — tidak memanggil LLM.
        Blok FAKTA adalah satu-satunya data yang boleh disebut model di kolom `reply`; angka di
        luar FAKTA ditolak kode (`sanitizeChatReply`).
      </p>
      {tenants.length === 0 ? (
        <p style={styles.hint}>Belum ada tenant terdaftar.</p>
      ) : (
        <div style={styles.row}>
          <select
            value={selected}
            onChange={(e) => setTenantId(e.target.value)}
            style={{ ...styles.input, width: '16rem' }}
          >
            {tenants.map((t: any) => (
              <option key={t._id} value={t._id}>
                {t.name}
              </option>
            ))}
          </select>
          <button onClick={() => void load()} style={styles.btn} disabled={busy || !selected}>
            {busy ? '⏳ Memuat…' : '🔍 Tampilkan prompt'}
          </button>
        </div>
      )}
      {error && <p style={styles.error}>{error}</p>}
      {preview && (
        <div style={styles.card}>
          <div style={{ ...styles.cardHeader, cursor: 'default' }}>
            <div>
              <strong>{preview.tenantName}</strong>{' '}
              <span style={styles.slug}>
                asisten {preview.assistantName} · {preview.todayLabel} · MENU {preview.menuShown}/
                {preview.menuFetched} produk
                {preview.chatStyleVariants
                  ? ` · gaya obrolan bergilir (${preview.chatStyleVariants} pilihan, contoh: ${preview.chatStyleHint})`
                  : ''}
              </span>
            </div>
            <span style={styles.slug}>
              {formatLocalDateTime(preview.generatedAt, GATEWAY_LOG_TIMEZONE)}
            </span>
          </div>
          <div style={styles.cardBody}>
            <h4 style={styles.h4}>Blok FAKTA (boleh disebut model)</h4>
            <pre style={styles.preBlock}>{preview.factsBlock}</pre>
            <h4 style={styles.h4}>System prompt lengkap</h4>
            <pre style={styles.preBlockTall}>{preview.systemPrompt}</pre>
          </div>
        </div>
      )}

      <h2 style={styles.h2}>📜 Riwayat versi prompt (snapshot per deploy)</h2>
      <p style={styles.hint}>
        Tiap deploy menyimpan SNAPSHOT template prompt — bagian dinamis diganti placeholder{' '}
        {'{assistantName}'}/{'{todayLabel}'}/{'{menu}'}/{'{factsBlock}'} — sehingga yang dibandingkan
        antar waktu adalah perubahan PERILAKU bot, bukan data tenant hari itu. Klik satu versi untuk
        melihat diff vs template aktif.
      </p>
      {snapshots.length === 0 ? (
        <p style={styles.hint}>
          Belum ada snapshot — terisi otomatis oleh CI pada deploy berikutnya (panggilan
          promptSnapshots:capture tepat setelah npx convex deploy).
        </p>
      ) : (
        <div
          style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem', marginBottom: '0.75rem' }}
        >
          {snapshots.map((s: any) => {
            const isActive = s.templateHash === currentHash
            const isSelected = s.templateHash === selectedHash
            return (
              <div
                key={s._id}
                onClick={() => setSelectedHash(isSelected ? null : s.templateHash)}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '0.75rem',
                  flexWrap: 'wrap',
                  padding: '0.5rem 0.75rem',
                  border: `1px solid ${isSelected ? '#1f6feb' : '#30363d'}`,
                  borderRadius: '6px',
                  cursor: 'pointer',
                }}
              >
                <strong>v{s.version}</strong>
                <span style={styles.slug}>
                  {formatLocalDateTime(s.firstSeenAt, GATEWAY_LOG_TIMEZONE)}
                  {s.lastSeenVersion !== s.version
                    ? ` → dipakai lagi ${s.lastSeenVersion}`
                    : ''}
                </span>
                <span style={styles.slug}>hash {s.templateHash}</span>
                <span style={styles.slug}>
                  {s.source}
                  {s.runId ? ` · run ${s.runId}` : ''}
                </span>
                {isActive && <span style={{ color: '#3fb950', fontWeight: 600 }}>● AKTIF</span>}
              </div>
            )
          })}
        </div>
      )}
      {selectedSnapshot && (
        <div style={styles.card}>
          <div style={{ ...styles.cardHeader, cursor: 'default' }}>
            <div>
              <strong>v{selectedSnapshot.version}</strong>{' '}
              <span style={styles.slug}>diff vs template aktif (hash {selectedSnapshot.templateHash})</span>
            </div>
            <span style={styles.slug}>
              +{historySummary.add} / −{historySummary.del} / ={historySummary.same} baris
            </span>
          </div>
          <div style={styles.cardBody}>
            <div style={{ ...styles.preBlockTall, whiteSpace: 'pre-wrap' }}>
              {historyLines.map((line, idx) => (
                <div
                  key={idx}
                  style={{
                    color:
                      line.op === 'add' ? '#3fb950' : line.op === 'del' ? '#f85149' : undefined,
                  }}
                >
                  {line.op === 'add' ? '+ ' : line.op === 'del' ? '- ' : '  '}
                  {line.text}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

// ─── Tab 📋 Log (10 Okt 2026) ────────────────────────────────────────────
// Semua log sistem dalam SATU tab dengan sub-tab per kategori:
//   🤖 AI Gateway  → tabel aiGatewayUsage (request LLM: status/HTTP/error/token)
//   🐞 Debug       → tabel debugLogs (troubleshoot bot: level/context/message)
//   🧾 Audit       → tabel auditLogs (aksi kritikal: create/update/delete)
// dulu debugLogs & auditLogs tidak pernah tampil di UI sama sekali, dan log AI
// cuma 20 baris terakhir di tab Usage Stats.
// 10 Okt 2026: tiap sub-tab dapat filter (status / level+context / action),
// pencarian teks bebas, dan tombol 🔄 Segarkan (resubscribe paksa — query Convex
// sebenarnya live; tombol ini remount `LogsData` tanpa reload halaman penuh).
const LOG_CATEGORIES = [
  { key: 'ai', label: '🤖 AI Gateway' },
  { key: 'debug', label: '🐞 Debug' },
  { key: 'audit', label: '🧾 Audit' },
] as const

type LogCategory = (typeof LOG_CATEGORIES)[number]['key']

/** Filter per sub-tab — di state LogsPanel supaya remount LogsData tidak menghapusnya. */
interface LogFilters {
  status: string
  level: string
  context: string
  action: string
}

const LOG_FILTERS_EMPTY: LogFilters = { status: 'all', level: 'all', context: 'all', action: 'all' }

/** Nilai distinct (string) satu field log yang termuat → opsi dropdown. */
function distinctLogValues(logs: any[], field: string): string[] {
  const values = new Set<string>()
  for (const log of logs) {
    const value = log?.[field]
    if (typeof value === 'string' && value) values.add(value)
  }
  return Array.from(values).sort()
}

/** Dropdown filter satu dimensi; "all" = tanpa filter. */
function LogFilterSelect({
  allLabel,
  value,
  options,
  onChange,
}: {
  allLabel: string
  value: string
  options: string[]
  onChange: (value: string) => void
}) {
  return (
    <select style={styles.select} value={value} onChange={(e) => onChange(e.target.value)}>
      <option value="all">{allLabel}</option>
      {options.map((option) => (
        <option key={option} value={option}>
          {option}
        </option>
      ))}
    </select>
  )
}

function LogsPanel({ token }: { token: string }) {
  const [cat, setCat] = useState<LogCategory>('ai')
  const [search, setSearch] = useState('')
  const [filters, setFilters] = useState<LogFilters>(LOG_FILTERS_EMPTY)
  const [refreshTick, setRefreshTick] = useState(0)

  const onFilter = (key: keyof LogFilters, value: string) =>
    setFilters((prev) => ({ ...prev, [key]: value }))

  return (
    <div style={styles.statsSection}>
      <div style={styles.sectionHeader}>
        <h2 style={styles.h2}>📋 Log Sistem</h2>
        <button style={styles.btnSm} onClick={() => setRefreshTick((t) => t + 1)}>
          🔄 Segarkan
        </button>
      </div>
      <p style={styles.hint}>
        🧹 Retensi otomatis: 🐞 Debug {DEBUG_LOG_RETENTION_DAYS} hari · 🧾 Audit detail{' '}
        {AUDIT_DETAIL_RETENTION_DAYS} hari → 📦 arsip ringkasan {AUDIT_ARCHIVE_RETENTION_DAYS} hari
        (kebijakan backend `convex/lib/logRetention.ts` — angka di sini dibaca dari modul yang
        sama, bukan salinan).
      </p>
      <div style={styles.subtabRow}>
        {LOG_CATEGORIES.map((c) => (
          <button
            key={c.key}
            onClick={() => setCat(c.key)}
            style={cat === c.key ? styles.chipActive : styles.chip}
          >
            {c.label}
          </button>
        ))}
      </div>
      <LogsData
        key={refreshTick}
        token={token}
        cat={cat}
        search={search}
        onSearch={setSearch}
        filters={filters}
        onFilter={onFilter}
      />
    </div>
  )
}

/**
 * Isi tab 📋 Log — satu-satunya pemilik 3 query log (≤200 baris terakhir).
 * Filter berjalan di KLIEN atas data yang termuat: tabel log tidak punya index
 * `level`/`context`/`action`, jadi filter server-side = scan penuh (mahal).
 */
function LogsData({
  token,
  cat,
  search,
  onSearch,
  filters,
  onFilter,
}: {
  token: string
  cat: LogCategory
  search: string
  onSearch: (value: string) => void
  filters: LogFilters
  onFilter: (key: keyof LogFilters, value: string) => void
}) {
  const aiLogs: any[] = useQuery(api.dashboard.listAiGatewayLogs, { sessionToken: token, limit: 200 }) ?? []
  const debugLogs: any[] = useQuery(api.dashboard.listDebugLogs, { sessionToken: token, limit: 200 }) ?? []
  const auditLogs: any[] = useQuery(api.dashboard.listAuditLogs, { sessionToken: token, limit: 200 }) ?? []
  // Arsip ringkasan audit (detail yang sudah lewat masa retensi) — bukti
  // jejak audit tetap utuh walau tabel detailnya dipangkas retensi harian.
  const auditArchives: any[] =
    useQuery(api.dashboard.listAuditArchives, { sessionToken: token, limit: 100 }) ?? []

  // Pencarian bebas: cocokkan seluruh row (JSON) — satu kotak cukup untuk
  // model/error/metadata tanpa satu field per kategori.
  const q = search.trim().toLowerCase()
  const matchesSearch = (log: any) => q === '' || JSON.stringify(log).toLowerCase().includes(q)
  const searchBox = (
    <input
      style={styles.logFilterInput}
      placeholder="🔍 Cari teks…"
      value={search}
      onChange={(e) => onSearch(e.target.value)}
    />
  )

  if (cat === 'ai') {
    const rows = aiLogs.filter(
      (l) => (filters.status === 'all' || l.status === filters.status) && matchesSearch(l),
    )
    return (
      <div>
        <div style={styles.logFilterRow}>
          {searchBox}
          <LogFilterSelect
            allLabel="Semua status"
            value={filters.status}
            options={distinctLogValues(aiLogs, 'status')}
            onChange={(v) => onFilter('status', v)}
          />
        </div>
        <h3 style={styles.h3}>
          AI Gateway — {rows.length} dari {aiLogs.length} log terakhir
        </h3>
        {rows.length === 0 && (
          <p style={styles.hint}>
            {aiLogs.length === 0 ? 'Belum ada request LLM tercatat.' : 'Tidak ada log yang cocok dengan filter.'}
          </p>
        )}
        {rows.map((l) => (
          <div key={l._creationTime} style={styles.logRow}>
            <span style={styles.logTime}>
              {formatLocalDateTime(l._creationTime, GATEWAY_LOG_TIMEZONE)}
            </span>
            <span>
              {l.status === 'success' ? '✅' : '❌'} <code>{l.providerSlug}/{l.model}</code>
            </span>
            <span style={styles.slug}>
              {l.latencyMs}ms
              {l.httpStatus ? ` · HTTP ${l.httpStatus}` : ''}
              {l.errorCategory ? ` · ${l.errorCategory}` : ''}
              {l.promptTokens != null || l.completionTokens != null
                ? ` · ${l.promptTokens ?? 0}↑ ${l.completionTokens ?? 0}↓`
                : l.status === 'success'
                  ? ' · token tidak dilaporkan provider'
                  : ''}
            </span>
            {l.error && <span style={styles.logError}>{l.error}</span>}
          </div>
        ))}
      </div>
    )
  }

  if (cat === 'debug') {
    const rows = debugLogs.filter(
      (l) =>
        (filters.level === 'all' || l.level === filters.level) &&
        (filters.context === 'all' || l.context === filters.context) &&
        matchesSearch(l),
    )
    return (
      <div>
        <div style={styles.logFilterRow}>
          {searchBox}
          <LogFilterSelect
            allLabel="Semua level"
            value={filters.level}
            options={distinctLogValues(debugLogs, 'level')}
            onChange={(v) => onFilter('level', v)}
          />
          <LogFilterSelect
            allLabel="Semua context"
            value={filters.context}
            options={distinctLogValues(debugLogs, 'context')}
            onChange={(v) => onFilter('context', v)}
          />
        </div>
        <h3 style={styles.h3}>
          Debug — {rows.length} dari {debugLogs.length} log terakhir
        </h3>
        {rows.length === 0 && (
          <p style={styles.hint}>
            {debugLogs.length === 0 ? 'Belum ada log debug.' : 'Tidak ada log yang cocok dengan filter.'}
          </p>
        )}
        {rows.map((l) => (
          <div key={l._creationTime} style={styles.logRow}>
            <span style={styles.logTime}>
              {formatLocalDateTime(l.timestamp ?? l._creationTime, GATEWAY_LOG_TIMEZONE)}
            </span>
            <span>
              {l.level === 'error' ? '❌' : l.level === 'warn' ? '⚠️' : 'ℹ️'}{' '}
              <code>{l.context}</code>
            </span>
            <span style={l.level === 'error' ? styles.logError : styles.slug}>{l.message}</span>
            {l.metadata && (
              <span style={styles.slug}>
                {JSON.stringify(l.metadata).slice(0, 140)}
              </span>
            )}
          </div>
        ))}
      </div>
    )
  }

  const rows = auditLogs.filter(
    (l) => (filters.action === 'all' || l.action === filters.action) && matchesSearch(l),
  )
  return (
    <div>
      <div style={styles.logFilterRow}>
        {searchBox}
        <LogFilterSelect
          allLabel="Semua aksi"
          value={filters.action}
          options={distinctLogValues(auditLogs, 'action')}
          onChange={(v) => onFilter('action', v)}
        />
      </div>
      <h3 style={styles.h3}>
        Audit — {rows.length} dari {auditLogs.length} log terakhir
      </h3>
      {rows.length === 0 && (
        <p style={styles.hint}>
          {auditLogs.length === 0 ? 'Belum ada aksi tercatat.' : 'Tidak ada log yang cocok dengan filter.'}
        </p>
      )}
      {rows.map((l) => (
        <div key={l._creationTime} style={styles.logRow}>
          <span style={styles.logTime}>
            {formatLocalDateTime(l.timestamp ?? l._creationTime, GATEWAY_LOG_TIMEZONE)}
          </span>
          <span>
            <code>{l.action}</code>
          </span>
          <span style={styles.slug}>
            {l.entityType}
            {l.entityId ? ` · ${String(l.entityId).slice(0, 12)}…` : ''}
          </span>
          {l.metadata && (
            <span style={styles.slug}>
              {JSON.stringify(l.metadata).slice(0, 140)}
            </span>
          )}
        </div>
      ))}

      <h3 style={styles.h3}>
        📦 Arsip ringkasan audit — {auditArchives.length} baris arsip terakhir
      </h3>
      <p style={styles.hint}>
        Baris detail audit lebih tua dari {AUDIT_DETAIL_RETENTION_DAYS} hari digulung ke sini
        (per tenant × hari × aksi: jumlah, waktu pertama–terakhir, contoh id) lalu detailnya
        dihapus — "berapa kali & kapan" tidak hilang, tabel detail tidak membengkak.
        Ringkasan disimpan {AUDIT_ARCHIVE_RETENTION_DAYS} hari.
      </p>
      {auditArchives.length === 0 ? (
        <p style={styles.hint}>
          Belum ada arsip — terisi otomatis saat ada audit lebih tua dari{' '}
          {AUDIT_DETAIL_RETENTION_DAYS} hari (cron `log-retention-daily`).
        </p>
      ) : (
        auditArchives.map((a) => (
          <div key={a._creationTime} style={styles.logRow}>
            <span style={styles.logTime}>{a.dayKey}</span>
            <span>
              <code>{a.action}</code>
            </span>
            <span style={styles.slug}>
              {a.count}× · {a.entityType ?? '—'}
              {a.sampleEntityIds?.length ? ` · ${a.sampleEntityIds.join(', ')}` : ''}
              {a.tenantId ? ` · tenant ${String(a.tenantId).slice(0, 8)}…` : ''}
            </span>
          </div>
        ))
      )}
    </div>
  )
}

function UsageStats({ token }: { token: string }) {
  const stats: any = useQuery(api.dashboard.getUsageStats, {
    sessionToken: token,
    limit: 100,
  })

  if (!stats) return <p style={styles.hint}>Loading usage...</p>

  const byProvider = (
    Object.entries(stats.byProvider ?? {}) as Array<
      [string, { total: number; success: number; error: number }]
    >
  ).sort((a, b) => b[1].total - a[1].total)
  const logs: any[] = stats.recentLogs ?? []
  const total = byProvider.reduce((sum, [, v]) => sum + v.total, 0)
  const success = byProvider.reduce((sum, [, v]) => sum + v.success, 0)
  const rate = total > 0 ? Math.round((success / total) * 100) : 0

  return (
    <div style={styles.statsSection}>
      <h2 style={styles.h2}>📊 Usage Stats</h2>
      <div style={styles.statsGrid}>
        <div style={styles.statCard}>
          <strong>{total}</strong>
          <span style={styles.slug}>request (100 log terakhir)</span>
        </div>
        <div style={styles.statCard}>
          <strong>{success}</strong>
          <span style={styles.slug}>sukses</span>
        </div>
        <div style={styles.statCard}>
          <strong>{rate}%</strong>
          <span style={styles.slug}>success rate</span>
        </div>
      </div>
      <div style={styles.successBar}>
        <div style={{ ...styles.successFill, width: `${rate}%` }} />
      </div>
      <h3 style={styles.h3}>Per provider</h3>
      {byProvider.length === 0 && <p style={styles.hint}>Belum ada pemakaian tercatat.</p>}
      {byProvider.map(([slug, v]) => (
        <div key={slug} style={styles.logRow}>
          <span>
            <strong>{slug}</strong>
          </span>
          <span style={styles.slug}>
            {v.total} req · {v.success} sukses · {v.error} gagal
          </span>
        </div>
      ))}
      <h3 style={styles.h3}>Log terakhir</h3>
      {logs.map((l) => (
        <div key={l._creationTime} style={styles.logRow}>
          <span style={styles.logTime}>
            {formatLocalDateTime(l._creationTime, GATEWAY_LOG_TIMEZONE)}
          </span>
          <span>
            {l.status === 'success' ? '✅' : '❌'} <code>{l.providerSlug}/{l.model}</code>
          </span>
          <span style={styles.slug}>
            {l.latencyMs}ms
            {l.httpStatus ? ` · HTTP ${l.httpStatus}` : ''}
            {l.errorCategory ? ` · ${l.errorCategory}` : ''}
            {l.promptTokens != null || l.completionTokens != null
              ? ` · ${l.promptTokens ?? 0}↑ ${l.completionTokens ?? 0}↓`
              : l.status === 'success'
                ? ' · token tidak dilaporkan provider'
                : ''}
          </span>
          {l.error && <span style={styles.logError}>{l.error}</span>}
        </div>
      ))}
    </div>
  )
}

// ─── Sidebar (10 Okt 2026) ─────────────────────────────
// SATU sidebar untuk semua role: daftar menu dibaca dari `dashboard/src/nav.ts`
// (`navForRole`), jadi dashboard OWNER nanti memakai komponen ini apa adanya
// dengan `role="owner"` — tidak ada menu kedua yang bisa drift.
// Responsif tanpa CSS media query: layout memakai flexWrap, jadi di 375px
// konten turun ke bawah sidebar tanpa overflow (DoD).
function Sidebar({ role, active, onSelect, onLogout, badges }: {
  role: DashboardRole
  active: string
  onSelect: (key: string) => void
  onLogout: () => void
  badges?: Record<string, string>
}) {
  const brand = dashboardBrand(role)
  return (
    <aside style={styles.sidebar}>
      <div style={styles.sidebarBrand}>
        <strong style={styles.sidebarTitle}>{brand.title}</strong>
        <span style={styles.sidebarSubtitle}>{brand.subtitle}</span>
      </div>
      <nav style={styles.sidebarNav}>
        {navForRole(role).map((item) => (
          <button
            key={item.key}
            onClick={() => onSelect(item.key)}
            title={item.hint}
            aria-current={item.key === active ? 'page' : undefined}
            style={item.key === active ? styles.navItemActive : styles.navItem}
          >
            <span style={styles.navIcon}>{item.icon}</span>
            <span style={styles.navLabel}>{item.label}</span>
            {badges?.[item.key] && <span style={styles.navBadge}>{badges[item.key]}</span>}
          </button>
        ))}
      </nav>
      <button onClick={onLogout} style={styles.sidebarLogout}>
        Logout
      </button>
    </aside>
  )
}

// ─── Panel 🏪 Toko (10 Okt 2026) ────────────────────────
// Nada bicara bot per TENANT. Halaman ini item menu PERTAMA yang berlaku untuk
// role owner (`dashboard/src/nav.ts`): pertanyaannya "bot saya bicara bagaimana
// ke pelanggan", bukan urusan teknis AI Gateway. Dashboard owner belum ada, jadi
// untuk sementara dibuka dari dashboard superadmin per tenant.
//
// Label/penjelasan nada TIDAK ditulis di sini — diimpor dari spec bersama
// `convex/lib/chatTone` yang juga dipakai bot & backend (satu sumber).
function TenantTonePanel({ token }: { token: string }) {
  const tenants: any[] =
    useQuery(api.dashboard.listTenantOptions, { sessionToken: token }) ?? []
  const [tenantId, setTenantId] = useState('')
  const selected = tenantId || tenants[0]?._id || ''
  const data: any = useQuery(
    api.dashboard.getTenantSettings,
    selected ? { sessionToken: token, tenantId: selected } : 'skip'
  )
  const setTone = useMutation(api.dashboard.setTenantTone)
  const [busy, setBusy] = useState('')
  const [error, setError] = useState('')
  const [saved, setSaved] = useState('')

  const apply = async (tone: string, reset: boolean) => {
    if (!selected) return
    setBusy(reset ? 'reset' : tone)
    setError('')
    setSaved('')
    try {
      const res = await setTone({
        sessionToken: token,
        tenantId: selected,
        tone,
        ...(reset ? { reset: true } : {}),
      })
      setSaved(
        reset
          ? `Nada ${res.toneLabel} dibuang — kembali ke bawaan untuk ${data?.tenantName ?? 'toko ini'}`
          : `Nada ${res.toneLabel} disimpan untuk ${data?.tenantName ?? 'toko ini'}`
      )
    } catch (err) {
      setError(userFacingErrorMessage(err, 'Gagal menyimpan nada bicara'))
    } finally {
      setBusy('')
    }
  }

  return (
    <div style={styles.statsSection}>
      <h2 style={styles.h2}>🏪 Nada bicara bot</h2>
      <p style={styles.hint}>
        Nada ini dipakai bot untuk semua balasan OBROLAN tenant ini (sapaan, jawaban ringan,
        penolakan ramah). Disimpan di <code>tenants.settings.tone</code> dan dikirim ke prompt
        sebagai aturan utama — gaya bahasa yang bergilir tiap pesan hanya boleh memilih kata DI
        DALAM nada ini. Aksi catat transaksi, angka, dan alur tidak berubah karena nada.
      </p>
      {tenants.length === 0 ? (
        <p style={styles.hint}>Belum ada tenant terdaftar.</p>
      ) : (
        <>
          <div style={styles.row}>
            <span style={styles.slug}>Toko</span>
            <select
              value={selected}
              onChange={(e) => {
                setTenantId(e.target.value)
                setSaved('')
                setError('')
              }}
              style={styles.select}
            >
              {tenants.map((t: any) => (
                <option key={t._id} value={t._id}>
                  {t.name}
                  {t.ownerName ? ` — ${t.ownerName}` : ''}
                </option>
              ))}
            </select>
          </div>
          {error && <p style={styles.error}>{error}</p>}
          {saved && <p style={styles.hint}>✅ {saved}</p>}
          {data === undefined ? (
            <p style={styles.slug}>Memuat setelan toko…</p>
          ) : (
            <>
              {CHAT_TONES.map((tone) => {
                const active = data?.tone === tone.key
                return (
                  <div key={tone.key} style={styles.card}>
                    <div style={{ ...styles.cardHeader, cursor: 'default' }}>
                      <div>
                        <strong>
                          {tone.emoji} {tone.label}
                        </strong>{' '}
                        {active ? (
                          <span style={{ ...styles.badge, background: '#3b82f6', marginLeft: 8 }}>
                            DIPAKAI
                          </span>
                        ) : (
                          <span style={{ ...styles.badgeGray, marginLeft: 8 }}>belum dipakai</span>
                        )}
                        <div style={styles.slug}>{tone.description}</div>
                      </div>
                      <button
                        onClick={() => void apply(tone.key, false)}
                        disabled={busy !== '' || active}
                        style={styles.btnSm}
                      >
                        {busy === tone.key ? 'Menyimpan…' : active ? 'Aktif' : 'Pakai'}
                      </button>
                    </div>
                  </div>
                )
              })}
              <p style={styles.hint}>
                Nama asisten: <strong>{data?.assistantName ?? '-'}</strong> — diubah lewat bot
                (menu <code>/start</code> → ✏️ Ubah), bukan dari sini.
              </p>
              <h3 style={styles.h3}>Teks nada yang disuntik ke prompt</h3>
              <pre style={preBase}>{data?.toneHint ?? ''}</pre>
              {data?.tone !== data?.toneDefault && (
                <button
                  onClick={() => void apply(data?.toneDefault ?? '', true)}
                  disabled={busy !== ''}
                  style={{ ...styles.btnSm, marginTop: '0.5rem' }}
                >
                  ↺ Kembalikan ke bawaan
                </button>
              )}
            </>
          )}
        </>
      )}
    </div>
  )
}

function Dashboard({ token, onLogout }: { token: string; onLogout: () => void }) {
  // Role dashboard: dashboard OWNER (menu laporan + langganan, menyusul) memakai
  // komponen & spec yang sama dengan `role="owner"` — item menunya sudah
  // terdaftar di dashboard/src/nav.ts (`navForRole('owner')`).
  const role: DashboardRole = 'superadmin'
  const [tab, setTab] = useState<string>(() => defaultNavKey(role))
  // Key tab yang BENAR-BENAR dirender: tab aktif yang tidak berlaku untuk role
  // ini (mis. sisa pilihan role lain) jatuh ke tab pertama role tersebut.
  const activeNav = navItemFor(role, tab)
  const current = activeNav?.key ?? defaultNavKey(role)
  // Sub-tab provider (di bawah tab utama): 'all' = tampilan tab utama,
  // selain itu = katalog model provider terpilih.
  const [providerTab, setProviderTab] = useState<string>('all')

  // Query REAKTIF (bukan polling 10 detik): setiap mutation langsung mendorong
  // data baru dari Convex, jadi enable/disable provider & simpan chain instan.
  const providers: any[] =
    useQuery(api.dashboard.listProviders, { sessionToken: token }) ?? []
  const rotationConfig: any = useQuery(api.dashboard.getRotationSettings, {
    sessionToken: token,
  })

  const activeProvider =
    current === 'providers' && providerTab !== 'all'
      ? providers.find((p: any) => p.slug === providerTab) ?? null
      : null

  return (
    <div style={styles.dashboard}>
      <div style={styles.shell}>
        <Sidebar
          role={role}
          active={current}
          onSelect={setTab}
          onLogout={onLogout}
          badges={{ providers: String(providers.length) }}
        />
        <main style={styles.content}>
          <header style={styles.header}>
            <div>
              <h1 style={styles.title}>
                {activeNav ? `${activeNav.icon} ${activeNav.label}` : '🔧 KasKita'}
              </h1>
              {activeNav && <p style={styles.subtitle}>{activeNav.hint}</p>}
            </div>
          </header>
      {/* Sub-tab: chip per provider HANYA di tab Providers. Di tab Model chip ini
          menyesatkan: klik provider bukan memfilter rotasi, tapi keluar dari tab Model. */}
      {current === 'providers' && (
        <div style={styles.subtabRow}>
          <button
            onClick={() => setProviderTab('all')}
            style={providerTab === 'all' ? styles.chipActive : styles.chip}
          >
            📦 Semua
          </button>
          {providers.map((p) => (
            <button
              key={p._id}
              onClick={() => setProviderTab(p.slug)}
              style={providerTab === p.slug ? styles.chipActive : styles.chip}
              title={`Katalog model ${p.label}`}
            >
              {p.label}{' '}
              <span style={styles.slug}>({(p.models as string[]).length})</span>
            </button>
          ))}
        </div>
      )}
      {activeProvider ? (
        <ProviderModelsPanel
          provider={activeProvider}
          token={token}
          chatChain={rotationConfig?.chat?.chain ?? []}
          visionChain={rotationConfig?.vision?.chain ?? []}
        />
      ) : current === 'providers' ? (
        <div>
          <AddProviderForm token={token} />
          {providers.map((p: any) => (
            <ProviderCard
              key={p._id}
              provider={p}
              token={token}
              chatChain={rotationConfig?.chat?.chain ?? []}
              visionChain={rotationConfig?.vision?.chain ?? []}
            />
          ))}
        </div>
      ) : current === 'model' ? (
        <ModelRotation providers={providers} token={token} config={rotationConfig} />
      ) : current === 'llm' ? (
        <LlmSettings token={token} providers={providers} />
      ) : current === 'toko' ? (
        <TenantTonePanel token={token} />
      ) : current === 'users' ? (
        <UsersPanel token={token} />
      ) : current === 'prompt' ? (
        <PromptPreview token={token} />
      ) : current === 'logs' ? (
        <LogsPanel token={token} />
      ) : current === 'stats' ? (
        <UsageStats token={token} />
      ) : (
        <p style={styles.hint}>Menu tidak dikenal — pilih menu di sidebar.</p>
      )}
        </main>
      </div>
    </div>
  )
}

// ─── App Root ──────────────────────────────────────────
//
// ErrorBoundary (5 Okt 2026): blank putih kedua kalinya — dulu akar masalah
// stub api buatan manual (lihat probe-dashboard-api), kini error query
// superadmin (mis. session token >24 jam → "Unauthorized") meledak saat
// render dan React meng-unmount seluruh tree tanpa jejak. Boundary ini
// menangkap error render, menampilkan pesan + tombol Aksi, dan bila error
// terindikasi sesi kedaluwarsa → logout otomatis (token dibuang, kembali
// ke form login) alih-alih layar kosong.
function isSessionExpiredError(message: string): boolean {
  return /Unauthorized|Session expired|Invalid session/i.test(message)
}

class ErrorBoundary extends Component<
  { onSessionExpired: () => void; children: ReactNode },
  { error: Error | null }
> {
  state = { error: null as Error | null }

  static getDerivedStateFromError(error: Error) {
    return { error }
  }

  componentDidCatch(error: Error, info: ErrorInfo) {
    console.error('[dashboard] render error', error, info.componentStack)
  }

  componentDidUpdate(_prev: Readonly<{ onSessionExpired: () => void }>, prevState: Readonly<{ error: Error | null }>) {
    // Tandai sesi kedaluwarsa SEKALI setelah error baru tercatat (bukan di
    // render — efek samping di render dilarang React).
    if (
      this.state.error &&
      !prevState.error &&
      isSessionExpiredError(this.state.error.message)
    ) {
      this.props.onSessionExpired()
    }
  }

  render() {
    const { error } = this.state
    if (error) {
      const sessionExpired = isSessionExpiredError(error.message)
      return (
        <div style={styles.loginWrap}>
          <div style={{ ...styles.card, maxWidth: 420, textAlign: 'center' }}>
            <h2 style={{ marginTop: 0 }}>
              {sessionExpired ? '🔐 Sesi berakhir' : '⚠️ Terjadi kesalahan'}
            </h2>
            <p style={styles.slug}>{sessionExpired
              ? 'Login superadmin kamu sudah kedaluwarsa (maks. 24 jam). Login ulang untuk melanjutkan.'
              : error.message}
            </p>
            <button
              onClick={() =>
                sessionExpired
                  ? this.props.onSessionExpired()
                  : this.setState({ error: null })
              }
              style={styles.btn}
            >
              {sessionExpired ? 'Login ulang' : 'Coba lagi'}
            </button>
          </div>
        </div>
      )
    }
    return this.props.children
  }
}

function AppInner() {
  const [token, setToken] = useState<string | null>(
    () => localStorage.getItem('kaskita_session')
  )

  const handleSessionExpired = () => {
    localStorage.removeItem('kaskita_session')
    setToken(null)
  }

  if (!token) {
    return <Login onLogin={setToken} />
  }

  return (
    <SessionGate token={token} onSessionExpired={handleSessionExpired} />
  )
}

// Gate sesi (6 Okt 2026): token lama yang kedaluwarsa/rusak membuat SEMUA
// query superadmin melempar "Server Error Called by client" — pesan server
// DIREDAKSI di production sehingga deteksi via teks error mustahil. Solusi:
// `auth.verifySession` (tidak melempar — return ok:false) dijalankan dulu
// SEBELUM query superadmin; gagal → langsung kembali ke form login.
function SessionGate({ token, onSessionExpired }: { token: string; onSessionExpired: () => void }) {
  const session: any = useQuery(api.auth.verifySession, { sessionToken: token })

  useEffect(() => {
    if (session && session.ok === false) onSessionExpired()
  }, [session, onSessionExpired])

  if (session === undefined) {
    return <div style={styles.slug}>Memeriksa sesi…</div>
  }
  if (session.ok === false) {
    return <Login onLogin={(t: string) => {
      localStorage.setItem('kaskita_session', t)
      window.location.reload()
    }} />
  }
  return (
    <ErrorBoundary onSessionExpired={onSessionExpired}>
      <Dashboard token={token} onLogout={onSessionExpired} />
    </ErrorBoundary>
  )
}

export default function App() {
  return (
    <ConvexProvider client={client}>
      <AppInner />
    </ConvexProvider>
  )
}

// ─── Styles ────────────────────────────────────────────
// Blok teks monospace panel 🧠 Prompt (FAKTA + system prompt) — satu basis agar
// dua blok tampil konsisten.
const preBase: React.CSSProperties = {
  margin: 0,
  padding: '0.6rem',
  background: '#0f172a',
  borderRadius: '6px',
  border: '1px solid #334155',
  color: '#e2e8f0',
  fontSize: '0.78rem',
  lineHeight: 1.5,
  whiteSpace: 'pre-wrap',
  wordBreak: 'break-word',
}

const styles: Record<string, React.CSSProperties> = {
  preBlock: preBase,
  preBlockTall: { ...preBase, maxHeight: '26rem', overflowY: 'auto' },
  loginWrap: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '100vh',
    background: '#0f172a',
    fontFamily: 'system-ui, -apple-system, sans-serif',
  },
  loginCard: {
    background: '#1e293b',
    padding: '2rem',
    borderRadius: '12px',
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
    width: '320px',
    boxShadow: '0 10px 30px rgba(0,0,0,0.5)',
  },
  title: { margin: 0, fontSize: '1.3rem', color: '#f1f5f9' },
  subtitle: { margin: 0, color: '#94a3b8', fontSize: '0.85rem' },
  input: {
    padding: '0.6rem 0.8rem',
    borderRadius: '6px',
    border: '1px solid #334155',
    background: '#0f172a',
    color: '#f1f5f9',
    fontSize: '0.9rem',
    width: '100%',
    boxSizing: 'border-box',
  },
  btn: {
    padding: '0.6rem 1rem',
    borderRadius: '6px',
    border: 'none',
    background: '#3b82f6',
    color: 'white',
    cursor: 'pointer',
    fontSize: '0.9rem',
    fontWeight: 600,
  },
  btnSm: {
    padding: '0.3rem 0.6rem',
    borderRadius: '4px',
    border: 'none',
    background: '#475569',
    color: 'white',
    cursor: 'pointer',
    fontSize: '0.75rem',
  },
  error: { color: '#ef4444', fontSize: '0.85rem', margin: 0 },
  dashboard: {
    minHeight: '100vh',
    background: '#0f172a',
    color: '#f1f5f9',
    fontFamily: 'system-ui, -apple-system, sans-serif',
    padding: '1rem',
    // Cukup lebar untuk sidebar + konten; di layar sempit flexWrap yang
    // menurunkan konten ke bawah sidebar (nol overflow di 375px).
    maxWidth: '1100px',
    margin: '0 auto',
  },
  // ── Sidebar (spec: dashboard/src/nav.ts) ────────
  shell: {
    display: 'flex',
    gap: '1rem',
    alignItems: 'flex-start',
    flexWrap: 'wrap' as const,
  },
  sidebar: {
    flex: '1 1 190px',
    minWidth: 0,
    maxWidth: '100%',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.35rem',
    background: '#1e293b',
    border: '1px solid #334155',
    borderRadius: '10px',
    padding: '0.75rem',
    boxSizing: 'border-box' as const,
  },
  sidebarBrand: {
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.15rem',
    marginBottom: '0.5rem',
  },
  sidebarTitle: { fontSize: '0.95rem', color: '#f1f5f9' },
  sidebarSubtitle: { fontSize: '0.7rem', color: '#64748b' },
  sidebarNav: { display: 'flex', flexDirection: 'column' as const, gap: '0.25rem' },
  navItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    width: '100%',
    padding: '0.5rem 0.6rem',
    borderRadius: '6px',
    border: '1px solid transparent',
    background: 'transparent',
    color: '#cbd5e1',
    cursor: 'pointer',
    fontSize: '0.85rem',
    textAlign: 'left' as const,
  },
  navItemActive: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    width: '100%',
    padding: '0.5rem 0.6rem',
    borderRadius: '6px',
    border: '1px solid #3b82f6',
    background: '#3b82f6',
    color: '#ffffff',
    cursor: 'pointer',
    fontSize: '0.85rem',
    fontWeight: 600,
    textAlign: 'left' as const,
  },
  navIcon: { fontSize: '1rem' },
  navLabel: { flex: 1 },
  navBadge: { fontSize: '0.7rem', color: '#bfdbfe' },
  sidebarLogout: {
    marginTop: '0.25rem',
    padding: '0.4rem 0.6rem',
    borderRadius: '6px',
    border: '1px solid #334155',
    background: 'transparent',
    color: '#94a3b8',
    cursor: 'pointer',
    fontSize: '0.8rem',
  },
  content: { flex: '3 1 480px', minWidth: 0 },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '1rem',
  },
  subtabRow: {
    display: 'flex',
    gap: '0.35rem',
    marginBottom: '1rem',
    flexWrap: 'wrap' as const,
  },
  chip: {
    padding: '0.3rem 0.7rem',
    borderRadius: '999px',
    border: '1px solid #334155',
    background: '#1e293b',
    color: '#94a3b8',
    cursor: 'pointer',
    fontSize: '0.8rem',
  },
  chipActive: {
    padding: '0.3rem 0.7rem',
    borderRadius: '999px',
    border: '1px solid #3b82f6',
    background: '#1e3b5f',
    color: '#60a5fa',
    cursor: 'pointer',
    fontSize: '0.8rem',
    fontWeight: 600,
  },
  modalBackdrop: {
    position: 'fixed' as const,
    inset: 0,
    background: 'rgba(2, 6, 23, 0.75)',
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    padding: '2rem 1rem',
    overflowY: 'auto' as const,
    zIndex: 50,
  },
  modalCard: {
    background: '#1e293b',
    borderRadius: '10px',
    padding: '1rem',
    width: '100%',
    maxWidth: '640px',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.5rem',
    boxShadow: '0 20px 50px rgba(0,0,0,0.6)',
  },
  pickerGroup: {
    border: '1px solid #334155',
    borderRadius: '8px',
    padding: '0.6rem 0.75rem',
    display: 'flex',
    flexDirection: 'column' as const,
    gap: '0.25rem',
  },
  checkRow: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.5rem',
    fontSize: '0.85rem',
    cursor: 'pointer',
  },
  subtabs: { display: 'flex', gap: '0.25rem', marginBottom: '0.5rem', flexWrap: 'wrap' as const },
  subtab: {
    padding: '0.35rem 0.8rem',
    borderRadius: '6px 6px 0 0',
    border: '1px solid #334155',
    borderBottom: 'none',
    background: '#1e293b',
    color: '#94a3b8',
    cursor: 'pointer',
    fontSize: '0.85rem',
  },
  subtabActive: {
    padding: '0.35rem 0.8rem',
    borderRadius: '6px 6px 0 0',
    border: '1px solid #3b82f6',
    borderBottom: 'none',
    background: '#1e3b5f',
    color: '#60a5fa',
    cursor: 'pointer',
    fontSize: '0.85rem',
    fontWeight: 600,
  },
  sectionHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    gap: '0.5rem',
    flexWrap: 'wrap' as const,
  },
  select: {
    padding: '0.35rem 0.5rem',
    borderRadius: '6px',
    border: '1px solid #334155',
    background: '#0f172a',
    color: '#f1f5f9',
    fontSize: '0.85rem',
  },
  card: {
    background: '#1e293b',
    borderRadius: '8px',
    marginBottom: '0.75rem',
    overflow: 'hidden',
  },
  cardHeader: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '0.75rem 1rem',
    cursor: 'pointer',
    borderBottom: '1px solid #334155',
  },
  cardBody: {
    padding: '0.75rem 1rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  badge: {
    padding: '2px 8px',
    borderRadius: '4px',
    fontSize: '0.7rem',
    fontWeight: 700,
    color: 'white',
  },
  badgeGray: {
    padding: '2px 8px',
    borderRadius: '4px',
    fontSize: '0.7rem',
    background: '#475569',
    color: 'white',
  },
  slug: { color: '#64748b', fontSize: '0.8rem' },
  row: {
    display: 'flex',
    gap: '0.5rem',
    alignItems: 'center',
    flexWrap: 'wrap' as const,
  },
  keysSection: {
    padding: '0.75rem 1rem',
    borderTop: '1px solid #334155',
  },
  h3: { margin: '0 0 0.5rem', fontSize: '1rem', color: '#e2e8f0' },
  h4: { margin: '0 0 0.5rem', fontSize: '0.9rem', color: '#94a3b8' },
  keyRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    padding: '0.5rem 0',
    borderBottom: '1px solid #334155',
  },
  keyInfo: { flex: 1 },
  keyActions: { display: 'flex', gap: '0.25rem', flexWrap: 'wrap' as const },
  errorText: { color: '#fca5a5', fontSize: '0.75rem', marginTop: '0.25rem' },
  addKeyForm: {
    marginTop: '0.75rem',
    padding: '0.75rem',
    background: '#0f172a',
    borderRadius: '6px',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
  },
  modelGroup: {
    background: '#1e293b',
    borderRadius: '8px',
    padding: '0.75rem 1rem',
    marginBottom: '0.75rem',
  },
  modelNum: {
    display: 'inline-block',
    width: '2.2rem',
    color: '#64748b',
    fontSize: '0.8rem',
    fontWeight: 700,
  },
  dragHandle: {
    cursor: 'grab',
    color: '#64748b',
    marginRight: '0.4rem',
    userSelect: 'none' as const,
  },
  keyRowDragging: {
    opacity: 0.4,
    borderTop: '2px dashed #3b82f6',
  },
  keyRowDropTarget: {
    borderTop: '2px solid #60a5fa',
  },
  stickyInput: {
    width: '4rem',
    padding: '0.35rem 0.5rem',
    borderRadius: '6px',
    border: '1px solid #334155',
    background: '#0f172a',
    color: '#f1f5f9',
    fontSize: '0.85rem',
  },
  hint: { color: '#94a3b8', fontSize: '0.8rem', margin: '0 0 0.75rem' },
  statsSection: { padding: '0.5rem 0' },
  h2: { fontSize: '1.1rem', color: '#e2e8f0' },
  statsGrid: {
    display: 'grid',
    gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))',
    gap: '0.75rem',
    marginBottom: '1rem',
  },
  statCard: {
    background: '#1e293b',
    padding: '0.75rem',
    borderRadius: '8px',
  },
  successBar: {
    height: '6px',
    background: '#334155',
    borderRadius: '3px',
    marginTop: '0.5rem',
    overflow: 'hidden',
  },
  successFill: {
    height: '100%',
    background: '#10b981',
    transition: 'width 0.3s',
  },
  logTable: {
    background: '#1e293b',
    borderRadius: '8px',
    padding: '0.5rem',
    maxHeight: '300px',
    overflowY: 'auto' as const,
  },
  // TelegramId bisa sangat panjang (suffix #deleted:<ts>) — wajib bisa patah
  // kata supaya tidak memicu overflow horizontal di 375px.
  breakAll: { wordBreak: 'break-all' as const },
  // Baris filter tab 📋 Log (cari + dropdown) — wrap di 375px tanpa overflow.
  logFilterRow: {
    display: 'flex',
    gap: '0.4rem',
    alignItems: 'center',
    flexWrap: 'wrap' as const,
    marginBottom: '0.5rem',
  },
  logFilterInput: {
    flex: '1 1 160px',
    minWidth: 0,
    padding: '0.35rem 0.5rem',
    borderRadius: '6px',
    border: '1px solid #334155',
    background: '#0f172a',
    color: '#f1f5f9',
    fontSize: '0.85rem',
  },
  logRow: {
    display: 'flex',
    gap: '0.5rem',
    alignItems: 'center',
    flexWrap: 'wrap' as const,
    padding: '0.25rem 0',
    borderBottom: '1px solid #334155',
    fontSize: '0.8rem',
  },
  // Timestamp kejadian WITA (UTC+8) — angka tabular supaya kolom jam sejajar.
  logTime: {
    color: '#94a3b8',
    fontSize: '0.72rem',
    fontVariantNumeric: 'tabular-nums',
    whiteSpace: 'nowrap' as const,
  },
  // Pesan error asli provider (dipotong) — akar masalah, mis. header kurang.
  logError: {
    flexBasis: '100%',
    color: '#f87171',
    fontSize: '0.72rem',
    wordBreak: 'break-word' as const,
  },
}
